import type { BasketLine } from "./commerceBasket.ts";
import { parseSavedCart, type SavedCart } from "./savedCart.ts";

export type CartSelection = Omit<BasketLine, "lineId" | "productId" | "priceId" | "quantity">;

export function cartLinePayload(line: BasketLine) {
  const { lineId, productId, priceId, quantity, ...selection } = line;
  return { lineId, productId, priceId, quantity: String(quantity), selection };
}

export function parseCartSelection(value: unknown): CartSelection {
  const s = value as CartSelection;
  const allowed = ["destinationCountryCode", "destinationRegionCode", "policyRevision", "promoCodes", "selectedPaymentMethodId", "fulfillmentMethod", "termsPlanId", "selectedShippingRateId"];
  if (!s || typeof s !== "object" || Array.isArray(s) || Object.keys(s).some(k => !allowed.includes(k)) ||
      !/^[A-Z]{2,3}$/.test(s.destinationCountryCode) || !Array.isArray(s.promoCodes) || s.promoCodes.length > 10 ||
      s.promoCodes.some(c => typeof c !== "string" || !/^[A-Z0-9_-]{1,64}$/.test(c)) || new Set(s.promoCodes).size !== s.promoCodes.length ||
      !["NONE", "DELIVERY", "PICKUP"].includes(s.fulfillmentMethod || "") ||
      (s.policyRevision !== undefined && (!Number.isSafeInteger(s.policyRevision) || s.policyRevision < 0)) ||
      [s.destinationRegionCode, s.selectedPaymentMethodId, s.termsPlanId, s.selectedShippingRateId].some(v => v !== undefined && (typeof v !== "string" || !v.trim() || v.length > 160))) throw new Error("Invalid cart selection");
  return s;
}

export function workingCartLines(cart: SavedCart): BasketLine[] {
  if (cart.status !== "OPEN") throw new Error("Cart is not open");
  return cart.lines.map(line => {
    const quantity = Number(line.quantity);
    if (!line.priceId || !Number.isSafeInteger(quantity) || quantity < 1 || quantity > 10000) throw new Error("Cart requires price or quantity selection");
    return { ...parseCartSelection(line.selection), lineId: line.lineId, productId: line.productId, priceId: line.priceId, quantity };
  });
}

export function parseCurrentCart(value: unknown, organizationId: string): SavedCart | null {
  if (!value || typeof value !== "object" || !("cart" in value)) throw new Error("Invalid current cart");
  if (value.cart === null) return null;
  const cart = parseSavedCart(value.cart, organizationId);
  workingCartLines(cart);
  return cart;
}
